import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import "../importInventory/importInventoryModal.scss";
import Loader from "../../common/Loader";
import { ApiGet, ApiPost, ApiPut } from "../../../helpers/API/ApiData";
import Success from "../../Toaster/Success/Success";
import { postImportAuditLog } from "../../../utils/importAuditLog.util";
import {
  buildServiceHeaderMap,
  canonicalizeDisplayName,
  classifyServiceRows,
  extractApiErrorMessage,
  normalizeLooseName,
  normalizeName,
  pickDefaultCategoryColor,
} from "../../../utils/serviceImport.util";

export default function ImportServicesModal({
  toggle,
  companyId,
  businessName,
  onImportComplete,
}) {
  const fileRef = useRef(null);
  const categoryMapRef = useRef({});
  const categoryInFlightRef = useRef({});
  const closeTimeoutRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const cancelImportRef = useRef(false);
  const logsRef = useRef([]);
  const flushTimerRef = useRef(null);
  const progressFlushTimerRef = useRef(null);
  const existingServiceByNameRef = useRef({});
  const countersRef = useRef({
    processed: 0,
    success: 0,
    failed: 0,
    skipped: 0,
    created: 0,
    updated: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toastmsg, setToastmsg] = useState("");
  const [er, setEr] = useState("");

  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [headerMap, setHeaderMap] = useState({});
  const [detectedHeaders, setDetectedHeaders] = useState([]);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState("upload");

  const [autoCreateMissing, setAutoCreateMissing] = useState(true);

  const [progress, setProgress] = useState({
    total: 0,
    processed: 0,
    success: 0,
    failed: 0,
    skipped: 0,
    created: 0,
    updated: 0,
    running: false,
  });
  const [logs, setLogs] = useState([]);

  const totalRows = rows?.length || 0;

  const canValidate = useMemo(() => {
    return (
      !!companyId &&
      totalRows > 0 &&
      !!headerMap?.serviceName &&
      !!headerMap?.category &&
      !progress.running
    );
  }, [companyId, headerMap, progress.running, totalRows]);

  const canConfirm =
    canValidate &&
    preview &&
    (preview.summary?.toCreate > 0 || preview.summary?.toUpdate > 0) &&
    step === "preview" &&
    !progress.running;

  const loadExistingServices = useCallback(async () => {
    const map = {};
    try {
      const res = await ApiGet(`service/company/${companyId}`);
      const list = res?.data?.data || [];
      for (const s of list) {
        if (s?.isActive === false) continue;
        const nameKey = normalizeName(s?.serviceName || "");
        const id = s?._id || s?.id;
        if (nameKey && id) map[nameKey] = { id, name: s.serviceName, service: s };
      }
    } catch (e) {
      // continue
    }
    existingServiceByNameRef.current = map;
    return map;
  }, [companyId]);

  const rebuildPreview = useCallback(
    (serviceMap) => {
      if (!rows?.length || !headerMap?.serviceName) {
        setPreview(null);
        return null;
      }
      const result = classifyServiceRows({
        rows,
        headerMap,
        autoCreateMissing,
        existingServiceByName: serviceMap || existingServiceByNameRef.current,
        categoryMap: categoryMapRef.current,
      });
      setPreview(result);
      setStep("preview");
      return result;
    },
    [rows, headerMap, autoCreateMissing]
  );

  useEffect(() => {
    let mounted = true;
    const fetchLists = async () => {
      if (!companyId) return;
      setIsLoading(true);
      try {
        let categories = [];
        try {
          const r = await ApiGet(`category/company/${companyId}`);
          categories = r?.data?.data || [];
        } catch (e) {
          categories = [];
        }

        const nextCategoryMap = {};
        (categories || []).forEach((c) => {
          const name =
            c?.categoryName || c?.name || c?.CategoryName || c?.title || "";
          const id = c?._id || c?.id || c?.categoryId;
          const keyStrict = normalizeName(name);
          const keyLoose = normalizeLooseName(name);
          if (id) {
            if (keyStrict) nextCategoryMap[keyStrict] = { id, name };
            if (keyLoose) nextCategoryMap[keyLoose] = { id, name };
          }
        });

        if (!mounted) return;
        categoryMapRef.current = nextCategoryMap;
        await loadExistingServices();
      } catch (e) {
        console.log("Failed to fetch service categories", e);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchLists();
    return () => {
      mounted = false;
      mountedRef.current = false;
      cancelImportRef.current = true;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, [companyId, loadExistingServices]);

  useEffect(() => {
    if (!success) return;
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setSuccess(false);
      setEr("");
      setToastmsg("");
    }, 5000);
  }, [success, er]);

  useEffect(() => {
    if (step !== "preview" || !rows.length) return;
    rebuildPreview(existingServiceByNameRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCreateMissing]);

  const scheduleFlushLogs = () => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      if (!mountedRef.current) return;
      setLogs(logsRef.current.slice(-200));
    }, 100);
  };

  const addLog = (entry) => {
    logsRef.current.push({ ts: Date.now(), ...entry });
    scheduleFlushLogs();
  };

  const scheduleFlushProgress = () => {
    if (progressFlushTimerRef.current) return;
    progressFlushTimerRef.current = setTimeout(() => {
      progressFlushTimerRef.current = null;
      if (!mountedRef.current) return;
      setProgress((p) => ({
        ...p,
        processed: countersRef.current.processed,
        success: countersRef.current.success,
        failed: countersRef.current.failed,
        skipped: countersRef.current.skipped,
        created: countersRef.current.created,
        updated: countersRef.current.updated,
      }));
    }, 120);
  };

  const bumpCounters = (delta) => {
    if (delta.processed) countersRef.current.processed += delta.processed;
    if (delta.success) countersRef.current.success += delta.success;
    if (delta.failed) countersRef.current.failed += delta.failed;
    if (delta.skipped) countersRef.current.skipped += delta.skipped;
    if (delta.created) countersRef.current.created += delta.created;
    if (delta.updated) countersRef.current.updated += delta.updated;
    scheduleFlushProgress();
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const apiPostWithRetry = async (path, payload, attempts = 4) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await ApiPost(path, payload);
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        const shouldRetry =
          status === 429 ||
          status === 502 ||
          status === 503 ||
          status === 504 ||
          !status;
        if (!shouldRetry) throw e;
        // eslint-disable-next-line no-await-in-loop
        await sleep(
          Math.min(1500, 250 * Math.pow(2, i)) + Math.floor(Math.random() * 120)
        );
      }
    }
    throw lastErr;
  };

  const apiPutWithRetry = async (path, payload, attempts = 4) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await ApiPut(path, payload);
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        const shouldRetry =
          status === 429 ||
          status === 502 ||
          status === 503 ||
          status === 504 ||
          !status;
        if (!shouldRetry) throw e;
        // eslint-disable-next-line no-await-in-loop
        await sleep(
          Math.min(1500, 250 * Math.pow(2, i)) + Math.floor(Math.random() * 120)
        );
      }
    }
    throw lastErr;
  };

  const resetStateForNewFile = () => {
    setRows([]);
    setHeaderMap({});
    setDetectedHeaders([]);
    setPreview(null);
    setStep("upload");
    setLogs([]);
    logsRef.current = [];
    countersRef.current = {
      processed: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      created: 0,
      updated: 0,
    };
    setProgress({
      total: 0,
      processed: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      created: 0,
      updated: 0,
      running: false,
    });
  };

  const onPickFile = () => {
    if (fileRef.current) fileRef.current.click();
  };

  const onFileChange = async (e) => {
    const f = e?.target?.files?.[0];
    if (!f) return;
    resetStateForNewFile();
    setFileName(f.name);

    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["xls", "xlsx"].includes(ext)) {
      setSuccess(true);
      setToastmsg("Please upload a .xls or .xlsx file");
      setEr("error");
      return;
    }

    setIsLoading(true);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) throw new Error("No sheet found in file");
      const ws = wb.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      const headers = json?.length > 0 ? Object.keys(json[0] || {}) : [];
      const hMap = buildServiceHeaderMap(headers);

      setDetectedHeaders(headers);
      setHeaderMap(hMap);
      setRows(json);

      if (!hMap.serviceName || !hMap.category) {
        addLog({
          type: "error",
          rowNumber: 0,
          message: "Missing required columns. Required: Service Name, Category.",
        });
      } else {
        const serviceMap = await loadExistingServices();
        const result = classifyServiceRows({
          rows: json,
          headerMap: hMap,
          autoCreateMissing,
          existingServiceByName: serviceMap,
          categoryMap: categoryMapRef.current,
        });
        setPreview(result);
        setStep("preview");
      }
    } catch (err) {
      setSuccess(true);
      setToastmsg(extractApiErrorMessage(err));
      setEr("error");
    } finally {
      setIsLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onValidateClick = async () => {
    if (!canValidate) return;
    setIsLoading(true);
    try {
      const serviceMap = await loadExistingServices();
      rebuildPreview(serviceMap);
      setSuccess(true);
      setToastmsg("Validation complete — review summary then Confirm import");
      setEr("");
    } catch (e) {
      setSuccess(true);
      setToastmsg(extractApiErrorMessage(e));
      setEr("error");
    } finally {
      setIsLoading(false);
    }
  };

  const resolveOrCreateCategory = async (categoryName, colourHint) => {
    const keyStrict = normalizeName(categoryName);
    const keyLoose = normalizeLooseName(categoryName);
    const existing =
      categoryMapRef.current[keyStrict] || categoryMapRef.current[keyLoose];
    if (existing?.id) return existing;
    if (!autoCreateMissing)
      throw new Error(`Category not found: ${categoryName}`);

    const lockKey = keyLoose || keyStrict;
    if (lockKey && categoryInFlightRef.current[lockKey]) {
      return await categoryInFlightRef.current[lockKey];
    }

    const canonicalName = canonicalizeDisplayName(categoryName);
    const payload = {
      categoryName: canonicalName,
      categoryColor:
        colourHint || pickDefaultCategoryColor(canonicalName),
      companyId,
    };

    const creatorPromise = (async () => {
      try {
        const res = await apiPostWithRetry("category/", payload);
        const status = res?.data?.status ?? res?.status;
        const created = res?.data?.data || res?.data?.data?.data || res?.data;
        const id = created?._id || created?.id || created?.data?._id;
        if (id && String(status) !== "208") {
          const name = created?.categoryName || payload.categoryName;
          const next = { id, name };
          categoryMapRef.current = {
            ...categoryMapRef.current,
            [keyStrict]: next,
            [keyLoose]: next,
          };
          return next;
        }

        // Already exists (208) or missing id — reload and resolve
        const r = await ApiGet(`category/company/${companyId}`);
        const categories = r?.data?.data || [];
        const nextMap = { ...categoryMapRef.current };
        let found = null;
        (categories || []).forEach((c) => {
          const n = c?.categoryName || c?.name || "";
          const cid = c?._id || c?.id;
          const ks = normalizeName(n);
          const kl = normalizeLooseName(n);
          if (cid) {
            if (ks) nextMap[ks] = { id: cid, name: n };
            if (kl) nextMap[kl] = { id: cid, name: n };
          }
          if (ks === keyStrict || kl === keyLoose) {
            found = { id: cid, name: n };
          }
        });
        categoryMapRef.current = nextMap;
        if (found?.id) return found;
        throw new Error(`Category create/resolve failed: ${categoryName}`);
      } catch (e) {
        const status = e?.response?.status || e?.response?.data?.status;
        if (String(status) === "208" || status === 208) {
          const r = await ApiGet(`category/company/${companyId}`);
          const categories = r?.data?.data || [];
          const nextMap = { ...categoryMapRef.current };
          let found = null;
          (categories || []).forEach((c) => {
            const n = c?.categoryName || c?.name || "";
            const cid = c?._id || c?.id;
            const ks = normalizeName(n);
            const kl = normalizeLooseName(n);
            if (cid) {
              if (ks) nextMap[ks] = { id: cid, name: n };
              if (kl) nextMap[kl] = { id: cid, name: n };
            }
            if (ks === keyStrict || kl === keyLoose) {
              found = { id: cid, name: n };
            }
          });
          categoryMapRef.current = nextMap;
          if (found?.id) return found;
        }
        throw e;
      }
    })();

    if (lockKey) categoryInFlightRef.current[lockKey] = creatorPromise;
    try {
      return await creatorPromise;
    } finally {
      if (lockKey) delete categoryInFlightRef.current[lockKey];
    }
  };

  const startImport = async () => {
    if (!canConfirm) return;

    const plan = preview;
    const actionable = (plan.planRows || []).filter(
      (r) => r.action === "create" || r.action === "update"
    );
    if (!actionable.length) {
      setSuccess(true);
      setToastmsg("Nothing to import — fix errors or choose another file");
      setEr("error");
      return;
    }

    logsRef.current = [];
    setLogs([]);
    cancelImportRef.current = false;
    countersRef.current = {
      processed: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      created: 0,
      updated: 0,
    };
    setStep("importing");
    setProgress({
      total: actionable.length,
      processed: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      created: 0,
      updated: 0,
      running: true,
    });

    (plan.planRows || [])
      .filter((r) => r.action === "error")
      .forEach((r) => {
        addLog({
          type: "skip",
          action: "error",
          rowNumber: r.excelRowNumber,
          name: r.serviceName,
          message: r.error || "Invalid row",
        });
        bumpCounters({ skipped: 1 });
      });

    for (let i = 0; i < actionable.length; i++) {
      if (cancelImportRef.current || !mountedRef.current) break;
      const planRow = actionable[i];
      const { parsed, excelRowNumber, action, existingId, serviceName } =
        planRow;
      try {
        if (!parsed) throw new Error("Missing parsed row");
        const category = await resolveOrCreateCategory(
          parsed.category,
          parsed.colour
        );
        if (!category?.id) throw new Error("Category create/resolve failed");

        const payload = {
          serviceName: parsed.serviceName,
          categoryId: category.id,
          companyId,
        };
        if (parsed.duration !== undefined) payload.duration = parsed.duration;
        if (parsed.amount !== undefined) payload.amount = parsed.amount;
        if (parsed.colour) payload.colour = parsed.colour;
        if (parsed.sacCode !== undefined) payload.sacCode = parsed.sacCode || "";

        if (action === "update" && existingId) {
          await apiPutWithRetry(`service/${existingId}`, payload);
          addLog({
            type: "success",
            action: "update",
            rowNumber: excelRowNumber,
            name: serviceName,
            message: `Updated: ${serviceName}`,
          });
          bumpCounters({ success: 1, updated: 1, processed: 1 });
        } else {
          if (payload.duration === undefined) payload.duration = 30;
          if (payload.amount === undefined) payload.amount = 0;
          await apiPostWithRetry("service/", payload);
          addLog({
            type: "success",
            action: "create",
            rowNumber: excelRowNumber,
            name: serviceName,
            message: `Created: ${serviceName}`,
          });
          bumpCounters({ success: 1, created: 1, processed: 1 });
        }
      } catch (err) {
        addLog({
          type: "error",
          action: "error",
          rowNumber: excelRowNumber,
          name: serviceName,
          message: extractApiErrorMessage(err),
        });
        bumpCounters({ failed: 1, processed: 1 });
      }
      // eslint-disable-next-line no-await-in-loop
      await sleep(80);
    }

    if (!mountedRef.current) return;
    scheduleFlushProgress();
    setProgress((p) => ({ ...p, running: false }));
    setStep("preview");

    const createdCount = countersRef.current.created;
    const updatedCount = countersRef.current.updated;
    const failedCount = countersRef.current.failed;
    const skippedCount = countersRef.current.skipped;
    const successCount = countersRef.current.success;

    const logResult = await postImportAuditLog({
      companyId,
      businessName,
      importType: "services",
      fileName,
      totalRecords: totalRows,
      successCount,
      failedCount,
      createdCount,
      updatedCount,
      skippedCount,
      logs: logsRef.current,
    });

    setSuccess(true);
    if (failedCount === 0) {
      setToastmsg(
        `Import completed. Created: ${createdCount}, Updated: ${updatedCount}`
      );
      setEr("");
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        if (typeof onImportComplete === "function") onImportComplete();
        toggle();
      }, 2500);
    } else {
      setToastmsg(
        `Import finished. Created: ${createdCount}, Updated: ${updatedCount}, Failed: ${failedCount}, Skipped: ${skippedCount}`
      );
      setEr("error");
      if (typeof onImportComplete === "function") onImportComplete();
    }

    if (!logResult.ok) {
      setTimeout(() => {
        if (!mountedRef.current) return;
        setSuccess(true);
        setToastmsg("Import done, but audit log could not be saved");
        setEr("error");
      }, 600);
    }
  };

  const percent =
    progress.total > 0
      ? Math.round((progress.processed / progress.total) * 100)
      : 0;
  const summary = preview?.summary;

  return (
    <>
      {isLoading && <Loader />}
      <div
        className="import-inventory-modal-backdrop"
        onClick={() => !progress.running && toggle()}
      >
        <div
          className="import-inventory-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="header">
            <h2>Import Services (Excel)</h2>
            <button
              className="close"
              onClick={() => !progress.running && toggle()}
            >
              ✕
            </button>
          </div>

          <div className="body">
            <div className="section">
              <div className="file-row">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xls,.xlsx"
                  style={{ display: "none" }}
                  onChange={onFileChange}
                />
                <button
                  className="primary"
                  onClick={onPickFile}
                  disabled={progress.running}
                >
                  Select Excel file
                </button>
                <div className="file-name">
                  {fileName ? fileName : "No file selected"}
                </div>
              </div>

              <div className="toggles">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={autoCreateMissing}
                    onChange={(e) => setAutoCreateMissing(e.target.checked)}
                    disabled={progress.running}
                  />
                  <span>Auto-create missing Category</span>
                </label>
              </div>
              <div className="hint" style={{ marginTop: 8 }}>
                Required columns: Service Name, Category. Optional: Duration,
                Amount, Colour, SAC Code.
              </div>
            </div>

            {summary && (
              <div className="section meta">
                <div className="small-title">Validation summary</div>
                <div className="meta-grid">
                  <div className="meta-item">
                    <div className="label">Will create</div>
                    <div className="value">{summary.toCreate}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Will update</div>
                    <div className="value">{summary.toUpdate}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Categories to create</div>
                    <div className="value">{summary.categoriesToCreate}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Errors</div>
                    <div className="value">{summary.errors}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Total rows</div>
                    <div className="value">{summary.totalRows}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="section meta">
              <div className="meta-grid">
                <div className="meta-item">
                  <div className="label">Rows detected</div>
                  <div className="value">{totalRows}</div>
                </div>
                <div className="meta-item">
                  <div className="label">Progress</div>
                  <div className="value">
                    {progress.running
                      ? `${progress.processed}/${progress.total} (${percent}%)`
                      : progress.total
                      ? `${progress.created} created / ${progress.updated} updated`
                      : "-"}
                  </div>
                </div>
                <div className="meta-item">
                  <div className="label">Created / Updated / Failed</div>
                  <div className="value">
                    {progress.created} / {progress.updated} / {progress.failed}
                  </div>
                </div>
              </div>
              {progress.total > 0 && (
                <div className="progress-bar">
                  <div className="progress" style={{ width: `${percent}%` }} />
                </div>
              )}
            </div>

            {preview?.planRows?.length > 0 && (
              <div className="section">
                <div className="small-title">Row review</div>
                <div className="review-table-wrap">
                  <table className="review-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Action</th>
                        <th>Service</th>
                        <th>Category</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.planRows.slice(0, 300).map((r) => (
                        <tr
                          key={`${r.excelRowNumber}-${r.action}`}
                          className={r.action}
                        >
                          <td>{r.excelRowNumber}</td>
                          <td>
                            <span className={`badge ${r.action}`}>
                              {r.action}
                            </span>
                          </td>
                          <td>{r.serviceName}</td>
                          <td>{r.category}</td>
                          <td>{r.error || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {detectedHeaders?.length > 0 && (
              <div className="section">
                <div className="small-title">Detected columns (first sheet)</div>
                <div className="chips">
                  {detectedHeaders.map((h) => (
                    <span key={h} className="chip">
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="section">
              <div className="small-title">Import log</div>
              <div className="log">
                {logs.length === 0 ? (
                  <div className="log-empty">No logs yet.</div>
                ) : (
                  logs.slice(-200).map((l) => (
                    <div
                      key={`${l.ts}-${l.rowNumber}-${l.type}`}
                      className={`log-row ${l.type}`}
                    >
                      <div className="row-no">
                        {l.rowNumber ? `Row ${l.rowNumber}` : "File"}
                      </div>
                      <div className="msg">{l.message}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="footer">
            <button
              className="secondary"
              onClick={() => !progress.running && toggle()}
            >
              Cancel
            </button>
            <button
              className="secondary"
              onClick={onValidateClick}
              disabled={!canValidate}
            >
              Validate
            </button>
            <button
              className="primary"
              onClick={startImport}
              disabled={!canConfirm && !progress.running}
            >
              {progress.running ? "Importing..." : "Confirm import"}
            </button>
          </div>
        </div>
      </div>

      {success && <Success modal={success} toastmsg={toastmsg} er={er} />}
    </>
  );
}
